import { type UIMessage, useChat } from "@ai-sdk/react";
import { DefaultChatTransport, isToolUIPart } from "ai";
import { ChevronLeft } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { FlotionMark } from "@/components/brand/FlotionLogo";
import { Button } from "@/components/ui/button";
import { ChatInput } from "./ChatInput";
import { ChatMessage } from "./ChatMessage";
import { ProgressCard, UploadCard, ViewResultsCard } from "./CreationCards";
import { HeroBackdrop } from "./HeroBackdrop";

/** Atlas "typing" indicator — three bouncing dots under the avatar. */
function AtlasTyping() {
  return (
    <div className="flex items-start gap-2.5">
      <span className="mt-0.5 grid size-7 shrink-0 place-items-center rounded-full bg-primary/15">
        <FlotionMark size={14} />
      </span>
      <div className="flex items-center gap-1 pt-3">
        <span className="size-1.5 animate-bounce rounded-full bg-muted-foreground" />
        <span className="size-1.5 animate-bounce rounded-full bg-muted-foreground [animation-delay:150ms]" />
        <span className="size-1.5 animate-bounce rounded-full bg-muted-foreground [animation-delay:300ms]" />
      </div>
    </div>
  );
}

interface CreationChatProps {
  initialMessage: string;
  onViewResults: (campaignId: string) => void;
  onCancel: () => void;
  onCampaignCreated?: () => void;
}

export function CreationChat({
  initialMessage,
  onViewResults,
  onCancel,
  onCampaignCreated,
}: CreationChatProps) {
  const [input, setInput] = useState("");
  const [campaignId, setCampaignId] = useState<string | null>(null);
  const [profileId, setProfileId] = useState<string | null>(null);
  const [showUpload, setShowUpload] = useState(false);
  const [showProgress, setShowProgress] = useState(false);
  const [showViewResults, setShowViewResults] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);
  const sentInitialRef = useRef(false);
  const pendingMessageRef = useRef("");
  const campaignIdRef = useRef<string | null>(null);
  const messagesRef = useRef<UIMessage[]>([]);

  // Keep refs in sync
  useEffect(() => {
    campaignIdRef.current = campaignId;
  }, [campaignId]);

  const transport = useMemo(
    () =>
      new DefaultChatTransport({
        api: "/api/creation-chat",
        prepareSendMessagesRequest: async () => {
          // Build history from current messages for context
          const history = messagesRef.current
            .filter((m) => m.role === "user" || m.role === "assistant")
            .map((m) => ({
              role: m.role,
              content:
                m.parts
                  ?.filter((p) => p.type === "text")
                  .map((p) => (p as any).text)
                  .join("") ?? "",
            }))
            .filter((m) => m.content);

          return {
            body: {
              message: pendingMessageRef.current,
              campaignId: campaignIdRef.current,
              history,
            },
          };
        },
      }),
    [],
  );

  const { messages, sendMessage, status, error } = useChat({
    id: "creation-chat",
    transport,
    onError: (err) => {
      toast.error(err.message || "Failed to send message");
    },
  });

  // Keep messagesRef in sync for transport
  useEffect(() => {
    messagesRef.current = messages;
  }, [messages]);

  const isLoading = status === "streaming" || status === "submitted";

  // Send initial message once on mount
  useEffect(() => {
    if (sentInitialRef.current) return;
    sentInitialRef.current = true;
    pendingMessageRef.current = initialMessage;
    sendMessage({ text: initialMessage });
  }, [initialMessage, sendMessage]);

  // Watch for tool outputs to detect campaign creation and trigger UI cards
  useEffect(() => {
    for (const msg of messages) {
      if (msg.role !== "assistant") continue;
      for (const part of msg.parts ?? []) {
        if (isToolUIPart(part) && part.state === "output-available") {
          const output = (part as any).output;
          if (output?.campaignId && !campaignId) {
            setCampaignId(output.campaignId);
            if (output.profileId) setProfileId(output.profileId);
            setShowUpload(true);
            onCampaignCreated?.();
          }
        }
      }
    }
  }, [messages, campaignId, onCampaignCreated]);

  // Auto-scroll
  // biome-ignore lint/correctness/useExhaustiveDependencies: intentionally re-run on card visibility changes
  useEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollTo({ top: el.scrollHeight, behavior: "smooth" });
  }, [messages, showUpload, showProgress, showViewResults]);

  const handleSubmit = useCallback(() => {
    const text = input.trim();
    if (!text || isLoading) return;
    setInput("");
    pendingMessageRef.current = text;
    sendMessage({ text });
  }, [input, isLoading, sendMessage]);

  const handleUploaded = useCallback(() => {
    setShowUpload(false);
    setShowProgress(true);
  }, []);

  const handleProcessingComplete = useCallback(() => {
    setShowViewResults(true);
  }, []);

  const lastMessage = messages[messages.length - 1];
  const assistantStreaming = status === "streaming" && lastMessage?.role === "assistant";
  // Waiting for the first token (or streaming hasn't produced an assistant bubble yet).
  const showTyping = isLoading && !assistantStreaming;

  return (
    <section className="relative flex flex-1 flex-col overflow-hidden bg-background">
      <HeroBackdrop showChrome={false} />
      {/* Stronger scrim than the hero for message legibility. */}
      <div className="absolute inset-0 z-[1] bg-gradient-to-b from-background/85 via-background/80 to-background/92" />

      {/* Header */}
      <div className="relative z-10 flex shrink-0 items-center gap-2 border-b border-border/70 px-4 py-2.5 backdrop-blur-sm">
        <Button variant="ghost" size="icon" className="h-7 w-7" onClick={onCancel}>
          <ChevronLeft className="h-4 w-4" />
        </Button>
        <FlotionMark size={20} />
        <span className="font-mono text-xs uppercase tracking-[0.2em] text-muted-foreground">
          New Campaign
        </span>
      </div>

      {/* Messages */}
      <div ref={scrollRef} className="relative z-10 flex-1 overflow-y-auto px-4 py-5">
        <div className="mx-auto w-full max-w-[820px] space-y-5">
          {messages.map((msg) => (
            <ChatMessage
              key={msg.id}
              message={msg}
              streaming={assistantStreaming && msg.id === lastMessage?.id}
            />
          ))}

          {showTyping && <AtlasTyping />}

          {/* Inline interactive cards — rendered in the Atlas column */}
          {showUpload && campaignId && (
            <div className="pl-[38px]">
              <UploadCard campaignId={campaignId} onUploaded={handleUploaded} />
            </div>
          )}

          {showProgress && campaignId && profileId && (
            <div className="pl-[38px]">
              <ProgressCard
                campaignId={campaignId}
                profileId={profileId}
                onComplete={handleProcessingComplete}
              />
            </div>
          )}

          {showViewResults && campaignId && (
            <div className="pl-[38px]">
              <ViewResultsCard campaignId={campaignId} onViewResults={onViewResults} />
            </div>
          )}

          {error && (
            <div className="rounded-lg border border-destructive/50 bg-destructive/10 px-3 py-2 text-xs text-destructive">
              {error.message}
            </div>
          )}
        </div>
      </div>

      {/* Input dock */}
      <div className="relative z-10 mx-auto w-full max-w-[820px] shrink-0">
        <ChatInput
          value={input}
          onChange={setInput}
          onSubmit={handleSubmit}
          isLoading={isLoading}
        />
      </div>
    </section>
  );
}
